import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Phone, FileText, Link2, MessageSquare, ArrowRight, CheckCircle2, Clock } from "lucide-react";
import { api } from "@/lib/api";
import { PageHeader } from "@/components/admin/DashboardLayout";
import { useAuth } from "@/context/AuthContext";

const GROUPS = [
  ["lead", "Clienti da sentire", "Persone che hanno lasciato un contatto sul sito. Prima vengono loro: dopo 24 ore l'interesse crolla.", Phone, "Apri scheda cliente"],
  ["content", "Articoli da pubblicare", "Bozze pronte. Leggi, correggi se serve, pubblica: ogni articolo porta visite da Google.", FileText, "Apri articolo"],
  ["affiliate", "Programmi affiliate da attivare", "Finché non sono attivi non guadagni sui click.", Link2, "Vai ai programmi"],
  ["contact", "Messaggi dal sito", "Richieste arrivate dal form Contatti.", MessageSquare, "Leggi messaggi"],
];
const PRIO = { alta: ["Urgente", "bg-rose-500/15 text-rose-700"], media: ["Oggi", "bg-amber-500/15 text-amber-700"], bassa: ["Quando puoi", "bg-sky-500/15 text-sky-700"] };

function Task({ t, onDone, Icon, action, idx }) {
  const [label, cls] = PRIO[t.priority] || PRIO.media;
  return (
    <div className="flex gap-3 sm:gap-4 px-4 sm:px-5 py-4" data-testid={`task-card-${idx}`}>
      <button onClick={() => onDone(t.id)} className="mt-0.5 grid h-6 w-6 shrink-0 place-items-center rounded-full border-2 border-border hover:border-emerald-500 hover:bg-emerald-500/10 transition-colors" aria-label="Segna come fatto" title="Segna come fatto" data-testid={`task-card-complete-btn-${idx}`}>
        <CheckCircle2 size={14} className="opacity-0 hover:opacity-100 text-emerald-600" />
      </button>
      <div className="min-w-0 flex-1">
        <div className="flex flex-wrap items-center gap-2">
          <p className="font-semibold text-base leading-snug">{t.title}</p>
          <span className={`rounded-full px-2 py-0.5 text-[11px] font-semibold ${cls}`}>{label}</span>
        </div>
        <p className="text-sm text-muted-foreground mt-1 leading-relaxed"><span className="font-medium text-foreground/80">Perché:</span> {t.why}</p>
        <p className="text-sm text-muted-foreground mt-0.5 leading-relaxed"><span className="font-medium text-foreground/80">Cosa fare:</span> {t.how}</p>
        {t.contact && <p className="font-mono text-xs text-emerald-700 mt-1.5 break-all" data-testid={`task-contact-${idx}`}>{t.contact}</p>}
        <div className="flex flex-wrap gap-2 mt-3">
          <Link to={t.link} className="btn-primary !py-1.5 !px-3 text-xs" data-testid={`task-open-${idx}`}>{action} <ArrowRight size={13} /></Link>
          <button onClick={() => onDone(t.id)} className="btn-ghost !py-1.5 !px-3 text-xs" data-testid={`task-done-${idx}`}>Fatto ✓</button>
        </div>
      </div>
      <span className="hidden sm:grid h-9 w-9 shrink-0 place-items-center rounded-lg bg-muted text-muted-foreground"><Icon size={16} /></span>
    </div>
  );
}

export default function Today() {
  const { user } = useAuth();
  const [data, setData] = useState(null);
  const [done, setDone] = useState(() => JSON.parse(localStorage.getItem("pp_done") || "[]"));
  useEffect(() => { api.get("/admin/today").then((r) => setData(r.data)).catch(() => setData({ tasks: [], counts: {} })); }, []);

  const markDone = (id) => { const n = [...done, id]; setDone(n); localStorage.setItem("pp_done", JSON.stringify(n)); };
  const tasks = (data?.tasks || []).filter((t) => !done.includes(t.id));
  const c = data?.counts || {};
  const urgent = tasks.filter((t) => t.priority === "alta").length;
  let idx = 0;

  return (
    <div data-testid="today-page">
      <PageHeader eyebrow={`Ciao ${user?.name}`} title="Cosa fare oggi" help="Qui trovi solo le cose che richiedono te. Tutto il resto (email ai clienti, report, promemoria rinnovi) parte da solo.">
        <Link to="/dashboard/content" className="btn-ghost !py-2 text-sm" data-testid="quick-new-article"><FileText size={14} /> Nuovo articolo</Link>
        <Link to="/" target="_blank" className="btn-ghost !py-2 text-sm" data-testid="quick-view-site">Vedi il sito</Link>
      </PageHeader>

      <div className="surface p-5 mb-6 flex flex-wrap items-center gap-x-6 gap-y-2" data-testid="today-summary">
        <p className="font-display text-xl font-bold">{data ? (tasks.length === 0 ? "Tutto fatto per oggi" : `${tasks.length} ${tasks.length === 1 ? "cosa" : "cose"} da fare`) : "Caricamento…"}</p>
        {urgent > 0 && <p className="inline-flex items-center gap-1.5 text-sm text-rose-700"><Clock size={14} /> {urgent} {urgent === 1 ? "urgente" : "urgenti"}: inizia da qui</p>}
        <div className="ml-auto flex flex-wrap gap-x-4 gap-y-1 text-sm text-muted-foreground">
          {[["clienti da sentire", c.lead_nuovi, "/dashboard/crm", "lead"], ["articoli da pubblicare", c.bozze, "/dashboard/content", "drafts"], ["programmi da attivare", c.network_inattivi, "/dashboard/affiliates", "networks"], ["messaggi", c.messaggi, "/dashboard/crm?tab=messaggi", "messages"]].map(([l, v, to, id]) => (
            <Link key={id} to={to} className="hover:text-emerald-700" data-testid={`today-count-${id}`}><b className="text-foreground">{v ?? 0}</b> {l}</Link>
          ))}
        </div>
      </div>

      {data && tasks.length === 0 && (
        <div className="surface p-10 text-center" data-testid="today-empty">
          <CheckCircle2 size={36} className="mx-auto text-emerald-500 mb-3" />
          <p className="font-display text-lg font-semibold">Non c'è nulla che richieda te adesso.</p>
          <p className="text-sm text-muted-foreground mt-1">Idee per usare bene il tempo: scrivi un articolo dalle <Link to="/dashboard/keywords" className="underline">Idee articoli</Link>, oppure condividi il calcolatore in un gruppo Facebook di proprietari di animali.</p>
        </div>
      )}

      <div className="space-y-6">
        {GROUPS.map(([type, title, help, Icon, action]) => {
          const list = tasks.filter((t) => t.type === type);
          if (!list.length) return null;
          return (
            <section key={type} className="surface divide-y divide-border" data-testid={`today-group-${type}`}>
              <div className="px-4 sm:px-5 py-4 flex items-start gap-3">
                <span className="grid h-9 w-9 shrink-0 place-items-center rounded-lg bg-emerald-500/10 text-emerald-600"><Icon size={16} /></span>
                <div><p className="font-semibold">{title} <span className="font-mono text-xs text-muted-foreground ml-1">{list.length}</span></p><p className="text-xs text-muted-foreground mt-0.5">{help}</p></div>
              </div>
              {list.map((t) => { idx += 1; return <Task key={t.id} t={t} idx={idx} onDone={markDone} Icon={Icon} action={action} />; })}
            </section>
          );
        })}
      </div>
    </div>
  );
}
